import { FormEvent, InputHTMLAttributes, RefObject, useRef } from 'react';
import styled from 'styled-components';

import MaterialIcon from '@/components/atom/MaterialIcon';
import theme from '@/styles/theme';

export interface DNSearchBarProps extends InputHTMLAttributes<HTMLInputElement> {
  searchInputRef?: RefObject<HTMLInputElement>;
  shouldShowResetButton?: boolean;
  onSubmit: () => void;
  onReset: () => void;
}

const DNSearchBar = ({
  searchInputRef,
  shouldShowResetButton = false,
  onSubmit,
  onReset,
  ...props
}: DNSearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const ref = searchInputRef ?? inputRef;
  const onClickSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    ref.current?.blur();
    onSubmit();
  };

  return (
    <SearchInputWrapper>
      <form onSubmit={onClickSubmit}>
        <SearchInputStyled tabIndex={0} ref={searchInputRef ?? inputRef} {...props} />
        <IconContainer>
          {shouldShowResetButton && (
            <IconWrapper type='reset' onClick={onReset}>
              <MaterialIcon name={'cancel'} color={theme.color.gray600} />
            </IconWrapper>
          )}
          <IconWrapper type='submit'>
            <MaterialIcon name={'search'} color={theme.color.gray600} />
          </IconWrapper>
        </IconContainer>
      </form>
    </SearchInputWrapper>
  );
};

export default DNSearchBar;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInputStyled = styled.input`
  ${({ theme }) => `
    ${theme.typography.UI16SB};
    background: ${theme.color.gray40};
    color: ${theme.color.gray700};
    &::placeholder {
      color: ${theme.color.gray300};
    }
    padding: 0 76px 0 16px;
  `};

  border: none;
  height: 48px;
  width: 100%;
  border-radius: 8px;

  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    font-size: 16px;
  }
`;

const IconContainer = styled.div`
  display: flex;
  position: absolute;
  right: 12px;
  border: none;
  top: 50%;
  transform: translate(0, -50%);
  background: none;
}
`;

const IconWrapper = styled.button`
  padding: 2px;
  border-radius: 8px;

  ${({ theme }) => `
    &: hover {
      background: ${theme.color.gray30};
    };
    &: active {
      background: ${theme.color.gray50};
    }
  `};
`;
